// Shared hook so the dashboard and the layout header use the same query/cache.

import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import { getSocket } from './socket';
import type { UnreadInfo } from '../types';

export const UNREAD_KEY = ['unread'] as const;

export function useUnread() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: UNREAD_KEY,
    queryFn: () => api<UnreadInfo>('/api/v1/notifications/unread'),
    refetchInterval: 30_000,
  });

  useEffect(() => {
    const s = getSocket();
    const invalidate = () => qc.invalidateQueries({ queryKey: UNREAD_KEY });
    s.on('message:new', invalidate);
    s.on('message:status', invalidate);
    return () => {
      s.off('message:new', invalidate);
      s.off('message:status', invalidate);
    };
  }, [qc]);

  // Mirror totalUnread to the PWA app icon badge (iOS 16.4+, Android, desktop).
  // Falls back silently on unsupported platforms.
  const total = q.data?.totalUnread;
  useEffect(() => {
    if (typeof navigator === 'undefined' || total == null) return;
    const nav = navigator as Navigator & {
      setAppBadge?: (n: number) => Promise<void>;
      clearAppBadge?: () => Promise<void>;
    };
    if (total > 0) {
      nav.setAppBadge?.(total).catch(() => {});
    } else {
      nav.clearAppBadge?.().catch(() => {});
    }
  }, [total]);

  return q;
}
